import React, { useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { DateTime } from 'luxon';
import frLocale from '@fullcalendar/core/locales/fr';
import koLocale from '@fullcalendar/core/locales/ko';
import { useSwipeable } from 'react-swipeable';

import './ArtistAvailabilityCalendar.css';

const DEFAULT_TIMEZONE = 'Europe/Paris';

// 다국어 텍스트
const TEXT = {
  title: {
    fr: 'Calendrier de disponibilité',
    ko: '예약 가능 일정',
    en: 'Availability Calendar'
  },
  today: {
    fr: 'AUJOURD\'HUI',
    ko: '오늘',
    en: 'TODAY'
  },
  available: {
    fr: 'Disponible',
    ko: '예약 가능',
    en: 'Available'
  },
  unavailable: {
    fr: 'Indisponible',
    ko: '예약 불가',
    en: 'Unavailable'
  },
  language: {
    fr: 'Français',
    ko: '한국어',
    en: 'English'
  },
  footer: {
    availableDates: {
      fr: 'Dates avec chiffres : disponibles',
      ko: '숫자 표시된 날짜 : 가능',
      en: 'Dates with numbers : available'
    },
    deposit: {
      fr: 'Un acompte sera demandé lors de la confirmation de réservation.',
      ko: '예약확정시 일정액의 선금이 요구됩니다.',
      en: 'A deposit will be required upon reservation confirmation.'
    },
    address: {
      fr: 'GNEGNEGNE\n4 rue Dunois 75013 PARIS',
      ko: 'GNEGNEGNE\n4 rue Dunois 75013 PARIS',
      en: 'GNEGNEGNE\n4 rue Dunois 75013 PARIS'
    },
    copyLink: {
      fr: 'Copier le lien du calendrier',
      ko: '캘린더 링크 복사하기',
      en: 'Copy calendar link'
    }
  }
};



export default function GuestAvailabilityCalendar() {
  const artistName = 'guest'; // 고정된 guest 유저네임 사용

  const calendarRef = useRef();
  const [blockDates, setBlockDates] = useState([]); // yyyy-MM-dd
  const [dayOffDates, setDayOffDates] = useState([]); // yyyy-MM-dd
  const [blockEvents, setBlockEvents] = useState([]);
  const [dayOffEvents, setDayOffEvents] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [currentMonthYear, setCurrentMonthYear] = useState('');
  const [artistId, setArtistId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedDate, setSelectedDate] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedText, setSelectedText] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [dispoDate, setDispoDate] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showDispo, setShowDispo] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('fr'); // 언어 상태 추가 (프랑스어 기본)
  const [isSwiping, setIsSwiping] = useState(false); // 스와이프 애니메이션 상태
  const [swipeDirection, setSwipeDirection] = useState(null); // 스와이프 방향
  const [copySuccess, setCopySuccess] = useState(false); // 복사 성공 상태

  // 언어 선택 핸들러
  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
  };

  // 스와이프 핸들러
  const handleSwipe = (direction) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      // 애니메이션 시작
      setIsSwiping(true);
      setSwipeDirection(direction);

      if (direction === 'left') {
        // 다음 달로
        calendarApi.next();
      } else if (direction === 'right') {
        // 이전 달로
        calendarApi.prev();
      }

      // 애니메이션 완료 후 상태 리셋
      setTimeout(() => {
        setIsSwiping(false);
        setSwipeDirection(null);
      }, 400);
    }
  };

  // react-swipeable 설정
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleSwipe('left'),
    onSwipedRight: () => handleSwipe('right'),
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
    delta: 50, // 최소 스와이프 거리
    swipeDuration: 500, // 최대 스와이프 시간
  });



  // 언어별 로케일 설정
  const getCalendarLocale = () => {
    switch (selectedLanguage) {
      case 'fr': return 'fr';
      case 'ko': return 'ko';
      default: return 'en';
    }
  };

  // 5달 범위 계산 (GuestCalendar용)
  const now = DateTime.now().setZone(DEFAULT_TIMEZONE).startOf('month');
  const minDate = now.toFormat('yyyy-MM-dd');
  const maxDate = now.plus({ months: 4 }).endOf('month').toFormat('yyyy-MM-dd');

  console.log('[Calendar Range]', { minDate, maxDate, now: now.toFormat('yyyy-MM') });

  // artistId 찾기 (artistName → user_id)
  useEffect(() => {
    if (!artistName) return;
    fetch('/api/public/allArtists')
      .then(res => res.json())
      .then(data => {
        const artists = Array.isArray(data.allArtists) ? data.allArtists : [];
        const found = artists.find(a => a.user_name === artistName);
        if (found) setArtistId(found.user_id);
      });
  }, [artistName]);

  // 블락(금지일) fetch (artistId와 무관)
  useEffect(() => {
    fetch(`/api/public/event-blocks?start=${minDate}&end=${maxDate}`)
      .then(res => res.json())
      .then(data => {
        console.log('[event-blocks 응답]', data);
        const events = (data.eventBlocks || []).map((block, idx) => {
          const blockId = block.block_id || `temp-${idx}`;
          const start = DateTime.fromISO(block.start_time, { zone: DEFAULT_TIMEZONE });
          const end = DateTime.fromISO(block.end_time, { zone: DEFAULT_TIMEZONE });
          let days = [];
          for (let d = start; d <= end; d = d.plus({ days: 1 })) {
            days.push(d.toFormat('yyyy-MM-dd'));
          }
          return {
            id: `block-${blockId}`,
            title: '',
            start: block.start_time,
            end: block.end_time,
            allDay: true,
            display: 'background',
            backgroundColor: 'rgba(255,0,0,0.18)',
            borderColor: 'rgba(255,0,0,0.18)',
            className: 'blocked-bg-event',
            is_restricted_period: true,
            blockDays: days,
          };
        });
        console.log('[blockEvents]', events);
        setBlockEvents(events);
        setBlockDates(events.flatMap(e => e.blockDays));
      });
  }, [minDate, maxDate]); // 다시 원래대로 변경

  // DayOff fetch (artistId가 있을 때만)
  useEffect(() => {
    if (!artistId) {
      setDayOffEvents([]);
      setDayOffDates([]);
      return;
    }
    fetch(`/api/artist-dayoff?artist_id=${artistId}&start=${minDate}&end=${maxDate}`)
      .then(res => res.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        const events = arr.map(d => {
          const dayStr = DateTime.fromISO(d.day).toFormat('yyyy-MM-dd');
          return {
            id: `dayoff-${dayStr}`,
            title: 'DayOff',
            start: dayStr,
            end: dayStr,
            allDay: true,
            display: 'background',
            backgroundColor: 'rgba(120,120,120,0.18)',
            textColor: '#888',
            fontWeight: 300,
            className: 'dayoff-bg-event',
            dayOffDay: dayStr,
          };
        });
        setDayOffEvents(events);
        setDayOffDates(events.map(e => e.dayOffDay));
      });
  }, [artistId, minDate, maxDate]); // 다시 원래대로 변경

  // 날짜 숫자 회색 처리 및 오늘 이전 날짜 회색 배경 처리
  const dayCellClassNames = arg => {
    const d = DateTime.fromJSDate(arg.date).toFormat('yyyy-MM-dd');
    const today = DateTime.now().setZone(DEFAULT_TIMEZONE).toFormat('yyyy-MM-dd');
    if (arg.isOther) return ['fc-other-month-cell'];
    if (blockDates.includes(d) || dayOffDates.includes(d)) return ['artist-fc-day-blocked'];
    if (d < today) return ['artist-fc-daygrid-day-past'];
    return [];
  };

  // 오늘 이전 날짜에 회색 배경 이벤트 추가
  const pastDaysEvents = (() => {
    const arr = [];
    const today = DateTime.now().setZone(DEFAULT_TIMEZONE).startOf('day');
    let d = DateTime.fromISO(minDate, { zone: DEFAULT_TIMEZONE });
    while (d < today) {
      arr.push({
        id: `past-${d.toFormat('yyyy-MM-dd')}`,
        start: d.toFormat('yyyy-MM-dd'),
        end: d.toFormat('yyyy-MM-dd'),
        allDay: true,
        display: 'background',
        backgroundColor: 'rgba(180,180,180,0.18)',
        className: 'artist-past-bg-event',
      });
      d = d.plus({ days: 1 });
    }
    return arr;
  })();

  // 타이틀
  let titleText = '';
  if (artistName) {
    switch (selectedLanguage) {
      case 'fr':
        titleText = `Disponibilités de ${artistName}`;
        break;
      case 'ko':
        titleText = `${artistName}의 예약 가능 일정`;
        break;
      default:
        titleText = `Availability of ${artistName}`;
        break;
    }
  }

  // 월/년 포맷
  const monthYearFormat = 'MMMM yyyy'; // luxon에서 MMMM은 월 이름
  // luxon의 locale 적용
  const getMonthYear = (date) => DateTime.fromJSDate(date).setLocale(selectedLanguage).toFormat(monthYearFormat);

  // FullCalendar 뷰/날짜 변경 시 월/년 갱신
  const handleDatesSet = (arg) => {
    setCurrentMonthYear(getMonthYear(arg.start));
  };

  // 이번달/다음달/다다음달 이동 함수 - 현재 미사용
  // eslint-disable-next-line no-unused-vars
  const goToMonth = offset => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    // 현재 보고 있는 달을 기준으로 이동
    const currentDate = DateTime.fromJSDate(api.getDate()).setZone(DEFAULT_TIMEZONE).startOf('month');
    const target = currentDate.plus({ months: offset });
    api.gotoDate(target.toJSDate());
    setCurrentMonthYear(getMonthYear(target.toJSDate()));
  };

  // FullCalendar 렌더 직전 이벤트 로그
  const allCalendarEvents = [
    ...blockEvents.map(e => ({ ...e, className: 'artist-blocked-bg-event' })),
    ...dayOffEvents.map(e => ({ ...e, className: 'artist-dayoff-bg-event' })),
    ...pastDaysEvents
  ];
  console.log('[FullCalendar events]', allCalendarEvents);

  // 언어별 메시지 함수 - 현재 미사용
  // eslint-disable-next-line no-unused-vars
  const getDisponibiliteText = (isBlocked) => {
    return isBlocked ? TEXT.unavailable[selectedLanguage] : TEXT.available[selectedLanguage];
  };

  // 언어별 DISPO 텍스트 - 현재 미사용
  // eslint-disable-next-line no-unused-vars
  const getDispoText = () => "DISPONIBLE";

  // 링크 복사 핸들러
  const handleCopyLink = async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // 2초 후 성공 메시지 숨김
    } catch (err) {
      console.error('링크 복사 실패:', err);
      // 폴백: 구형 브라우저 지원
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // 날짜 클릭 핸들러 (숫자 있는 날짜 클릭 시 아무 메시지도 표시하지 않음)
  const handleDateClick = (arg) => {
    const d = DateTime.fromJSDate(arg.date).toFormat('yyyy-MM-dd');
    const today = DateTime.now().setZone(DEFAULT_TIMEZONE).toFormat('yyyy-MM-dd');
    const isBlocked = blockDates.includes(d);
    const isDayOff = dayOffDates.includes(d);
    if (d < today || isBlocked || isDayOff) return;
    // 아무 메시지도 표시하지 않음
  };

  // 날짜 셀 커스텀 렌더링 (오늘은 TODAY, 오늘 이전/금지일/데이오프는 숫자 숨김, 그 외는 숫자 중앙정렬)
  const dayCellContent = (arg) => {
    const d = DateTime.fromJSDate(arg.date).toFormat('yyyy-MM-dd');
    const today = DateTime.now().setZone(DEFAULT_TIMEZONE).toFormat('yyyy-MM-dd');
    const isBlocked = blockDates.includes(d);
    const isDayOff = dayOffDates.includes(d);

    if (d === today) {
      const todayText = TEXT.today[selectedLanguage];
      // 텍스트가 길면 슬라이드 클래스 적용 (한국어 "오늘"은 2글자지만 짧아서 슬라이드 불필요)
      const shouldSlide = todayText.length > 4; // 4글자 이상이면 슬라이드 (TODAY, AUJOURD'HUI)

      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: shouldSlide ? 'flex-start' : 'center',
          fontWeight: 400,
          color: '#444',
          fontSize: '1em',
          letterSpacing: '0.04em',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          textAlign: shouldSlide ? 'left' : 'center',
          overflow: 'hidden',
          padding: shouldSlide ? '0 4px' : '0'
        }}>
          {shouldSlide ? (
            <div style={{
              whiteSpace: 'nowrap',
              animation: 'textSlide 4s ease-in-out infinite',
              display: 'inline-block',
              minWidth: 'max-content'
            }}>
              {todayText}
            </div>
          ) : (
            <span>{todayText}</span>
          )}
        </div>
      );
    }
    if (d < today || isBlocked || isDayOff) return "";
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1em',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        textAlign: 'center'
      }}>
        {arg.dayNumberText}
      </div>
    );
  };

  return (
    <div className="artist-availability-calendar-wrapper">
      {/* 언어 선택 UI - Calendar.jsx와 동일한 스타일 */}
      <div style={{
        display: 'flex',
        marginTop: '10px',
        marginBottom: '5px',
        gap: '0',
        justifyContent: 'flex-end',
        position: 'relative'
      }}>
        {/* 탭 컨테이너 */}
        <div
          style={{
            display: 'flex',
            gap: '0',
            justifyContent: 'center',
            border: '1px solid #000',
            borderRadius: '8px',
            padding: '0',
            transform: 'scale(0.75)',
            transformOrigin: 'right center'
          }}
        >
          {['en', 'fr', 'ko'].map((lang, index) => (
            <button
              key={lang}
              onClick={() => handleLanguageChange(lang)}
              style={{
                padding: '4px 16px',
                border: selectedLanguage === lang ? '1px solid #000' : '1px solid transparent',
                backgroundColor: '#fff',
                color: '#000',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '400',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: selectedLanguage === lang ? '1px solid #000' : '1px solid transparent',
                borderRight: selectedLanguage === lang ? '1px solid #000' : '1px solid transparent',
                transform: selectedLanguage === lang ? 'scale(1.15)' : 'scale(1)',
                zIndex: selectedLanguage === lang ? '2' : '1',
                ...(index === 0 && {
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px'
                }),
                ...(index === 2 && {
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px'
                })
              }}
            >
              {TEXT.language[lang]}
            </button>
          ))}
        </div>
      </div>
      <div style={{ textAlign: 'center', margin: '18px 0 21px', fontWeight: 300, fontSize: '1.13em' }}>{titleText}</div>
      <div
        {...swipeHandlers}
        className={`calendar-swipe-container ${isSwiping ? 'swiping' : ''} ${swipeDirection ? `swipe-${swipeDirection}` : ''}`}
        style={{ position: 'relative' }}
      >
        <FullCalendar
          key={blockDates.join(',') + dayOffDates.join(',') + (artistId || '')}
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{ start: 'prev', center: 'title', end: 'next' }}
          locales={[frLocale, koLocale]}
          locale={getCalendarLocale()}
          events={allCalendarEvents}
          editable={false}
          selectable={false}
          eventContent={() => null}
          dateClick={handleDateClick}
          dayCellContent={dayCellContent}
          eventClick={null}
          eventDrop={null}
          eventResize={null}
          validRange={{ start: minDate, end: maxDate }}
          dayCellClassNames={dayCellClassNames}
          height="auto"
          datesSet={handleDatesSet}
        />
      </div>

      {/* 달력 하단 텍스트 */}
      <div style={{
        marginTop: '30px',
        marginBottom: '40px',
        textAlign: 'left',
        lineHeight: '1.4'
      }}>
        <div style={{
          fontWeight: 300,
          color: '#000',
          marginBottom: '15px',
          fontSize: '0.9em'
        }}>
          {TEXT.footer.availableDates[selectedLanguage]}
        </div>
        <div style={{
          fontWeight: 300,
          color: '#000',
          fontSize: '0.85em'
        }}>
          {TEXT.footer.address[selectedLanguage].split('\n').map((line, index) => (
            <span key={index}>
              {line}
              {index < TEXT.footer.address[selectedLanguage].split('\n').length - 1 && <br />}
            </span>
          ))}
        </div>

        {/* 캘린더 링크 복사 버튼 */}
        <div style={{
          marginTop: '20px',
          textAlign: 'left',
          position: 'relative',
          zIndex: 10
        }}>
          <button
            onClick={handleCopyLink}
            style={{
              padding: '8px 16px',
              backgroundColor: copySuccess ? '#28a745' : '#fff',
              color: copySuccess ? '#fff' : '#000',
              border: '1px solid #000',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.85em',
              fontWeight: '400',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit',
              display: 'block',
              position: 'relative',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              if (!copySuccess) {
                e.target.style.backgroundColor = '#f8f9fa';
              }
            }}
            onMouseLeave={(e) => {
              if (!copySuccess) {
                e.target.style.backgroundColor = '#fff';
              }
            }}
          >
            {copySuccess
              ? (selectedLanguage === 'ko' ? '복사됨!' : selectedLanguage === 'fr' ? 'Copié!' : 'Copied!')
              : TEXT.footer.copyLink[selectedLanguage]
            }
          </button>
        </div>
      </div>
    </div>
  );
} 
